/**
 * Hover-prefetch helpers. The inside-ability list rows call these on mouseenter
 * (and focus, for keyboard nav) so the destination page's data is in the cache
 * by the time the user clicks. Combined with the module cache in `cache.ts`,
 * this makes drill-in clicks feel instant.
 *
 * All helpers are no-ops when the data is already cached, and silently swallow
 * errors — a failed prefetch shouldn't break anything since the page's own
 * mount will re-fetch on cache miss.
 *
 * NOTE: skill/agent content comes from the canonical item endpoint
 * (GET /api/items/:type/by-slug/:slug), NOT the removed per-ability /files
 * endpoint (which the consolidation migration stubbed to always return empty).
 * These prefetchers warm the SAME cache keys the drill-in pages read, so the
 * warm path is real rather than dead weight.
 */
import type { CatalogAbilityDetail } from "@bash/shared";
import { authedFetch } from "@/lib/api/abilities";
import { getItemBySlug } from "@/lib/api/items";
import { supabase } from "@/lib/supabase/client";
import { buildSkillFileList } from "./skillFiles";
import { parseAgentDetail } from "./agentDetail";
import { parseSkillDetail } from "./skillDetail";
import {
  getFilesCache,
  setFilesCache,
  getContentCache,
  setContentCache,
  setSkillDetailCache,
  getAgentDetailCache,
  setAgentDetailCache,
  getCachedDetail,
  setCachedDetail,
} from "./cache";

async function sessionToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export function prefetchSkill(abilityId: string, skillSlug: string) {
  const prefix = `skills/${skillSlug}/`;
  const skillMdPath = `${prefix}SKILL.md`;
  if (
    getFilesCache(abilityId, prefix) &&
    getContentCache(abilityId, skillMdPath)
  )
    return;
  (async () => {
    const token = await sessionToken();
    if (!token) return;
    const item = await getItemBySlug("skill", skillSlug, token);
    if (!item) return;
    setFilesCache(
      abilityId,
      prefix,
      buildSkillFileList(abilityId, skillSlug, item)
    );
    setContentCache(abilityId, skillMdPath, item.content ?? "");
    setSkillDetailCache(abilityId, skillSlug, {
      frontmatter: parseSkillDetail(item),
    });
  })().catch(() => {});
}

/**
 * Hover-prefetch helper for the catalog detail page. Called by ability cards
 * on `mouseenter` / focus so the destination /abilities/[slug] page's data is
 * already in the module cache by the time the user clicks. Combined with the
 * detail cache, drill-in clicks feel instant.
 *
 * Behaviour:
 *   - de-duplicates concurrent prefetches for the same slug via the
 *     `inflightAbilityDetail` map;
 *   - silently swallows errors (a failed prefetch is a no-op — the page mount
 *     re-fetches on cache miss);
 *   - no-op when the slug is already cached.
 */
const inflightAbilityDetail = new Map<string, Promise<void>>();

export function prefetchAbility(slug: string): Promise<void> {
  if (getCachedDetail(slug)) return Promise.resolve();
  const existing = inflightAbilityDetail.get(slug);
  if (existing) return existing;

  const p = authedFetch(`/api/abilities/catalog/${encodeURIComponent(slug)}`)
    .then((r) => r.json() as Promise<CatalogAbilityDetail>)
    .then((d) => {
      setCachedDetail(slug, d);
    })
    .catch(() => {
      // Swallow — the page mount will re-fetch on cache miss.
    })
    .finally(() => {
      inflightAbilityDetail.delete(slug);
    });
  inflightAbilityDetail.set(slug, p);
  return p;
}

export function prefetchAgent(abilityId: string, agentSlug: string) {
  if (getAgentDetailCache(abilityId, agentSlug)) return;
  (async () => {
    const token = await sessionToken();
    if (!token) return;
    const item = await getItemBySlug("agent", agentSlug, token);
    if (!item) return;
    setAgentDetailCache(
      abilityId,
      agentSlug,
      parseAgentDetail(agentSlug, item)
    );
  })().catch(() => {});
}
